import { toast } from 'react-toastify';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useStyles } from '@/styles/templateInputStyles';
import { RefundsApi } from '../../../api/clients/refunds/refunds.api';
import GridItem from '@/components/Grid/GridItem';
import Button from '@/components/CustomButtons/Button';
import { API_BASE_URL } from '@/config';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import { TextField } from '@mui/material';

import { useNavigate, useParams } from 'react-router-dom';
import FileConfiguration from './FileConfiguration';
import SftpPickUp from './SftpPickUp';
import BankAccountLinkage from './BankAccountLinkage';
import OutboundReporting from './OutboundReporting';
import CreateYourMapping from './CreateYourMapping';
import { REFUND_TYPES } from '../../../utility';
import { DataMappingsApi } from '@/api/clients/data-mappings/data-mappings.api';
import { checkPermission } from '@/views/Facilities/CheckPermissions';
import { useConfirmation } from '@/context/ConfirmationContext';
import { usePostHog } from 'posthog-js/react';
import { SftpConnectionsApi } from '@/api/clients/sftp-connections/sftp-connections.api';
import { useGetCustomPaymentTypesForForm } from '@/api/hooks/custom-payments/useGetCustomPaymentTypesForForm';
import useFormValidation from '@/hooks/useFormValidation';
import FormErrorSummary from '@/components/FormErrorSummary/FormErrorSummary';
import { toSnakeCasePath } from '@/utils/validationErrors';
import {
  fromSftpDestinationsResponse,
  toSftpDestinationsPayload,
  validateSftpDestinations,
} from './sftpDestinations';

const labelStyle = {
  fontSize: '12px',
  color: 'grey',
  cursor: 'pointer',
};

// INFO: Hand-synced with the JSX. Every field here renders its own inline
// error, so the create-mode summary can leave it out.
const INLINE_ERROR_FIELDS = new Set([
  // File Configuration
  'name',
  'delimiter',
  'contains_header',
  'rows_to_skip',
  'facility_id',
  // SFTP Pick Up
  'is_sftp_enabled',
  'client_sftp_connection_id',
  'file_source',
  'file_destination',
  'sftp_file_extension',
  'is_encrypted',
  // Bank Account Linkage
  'client_bank_account_id',
  // Create Your Mapping
  'refund_types',
  'custom_payment_type_ids',
  'regular_splitting_value',
  'business_splitting_value',
  'insurance_splitting_value',
  'immediate_payment_designators',
  'audience_designators',
  'is_single_audience',
  'audience_id',
  // Outbound Reporting
  'outbound_reporting',
  'generate_acknowledgement_file',
  'generate_daily_report',
  'sftp_destinations',
  'outbound_file_delimiter',
  'outbound_file_extension',
  'outbound_file_date_format',
  'encrypt_outbound',
  'include_headers_outbound',
  'reporting_columns',
  'acknowledgement_reporting_columns',
]);

const SFTP_DESTINATION_ROW_FIELD = /^sftp_destinations\[\d+\](\.(client_sftp_connection_id|acknowledgement(\.(file_path|file_name))?|daily_report(\.(file_location|file_name))?))?$/;

const DESTINATION_COVERAGE_FIELDS = new Set([
  'outbound_reporting',
  'generate_acknowledgement_file',
  'generate_daily_report',
]);

const rendersInline = (field) =>
  INLINE_ERROR_FIELDS.has(field) ||
  field.startsWith('custom_payment_types_mappings[') ||
  SFTP_DESTINATION_ROW_FIELD.test(field);

const OUTBOUND_REPORTING_FIELDS = [
  'outbound_reporting',
  'outbound_file_extension',
  'encrypt_outbound',
  'include_headers_outbound',
  'reporting_columns',
  'outbound_file_delimiter',
  'outbound_file_date_format',
  'generate_acknowledgement_file',
  'generate_daily_report',
  'acknowledgement_reporting_columns',
  'sftp_destinations',
];

const pickOutboundReportingFields = (values) =>
  Object.fromEntries(
    OUTBOUND_REPORTING_FIELDS.map((field) => [field, values[field]]),
  );

const NO_ERRORS = [];

const mapper = {
  [REFUND_TYPES.REGULAR]: {
    splittingValue: 'regular_splitting_value',
    dataMappingType: 'regular_data_mapping',
  },
  [REFUND_TYPES.INSURANCE]: {
    splittingValue: 'insurance_splitting_value',
    dataMappingType: 'insurance_data_mapping',
  },
  [REFUND_TYPES.BUSINESS]: {
    splittingValue: 'business_splitting_value',
    dataMappingType: 'business_data_mapping',
  },
};

export default function CreateOrEditDataMappings() {
  const { t } = useTranslation();
  const { requestConfirmation } = useConfirmation();
  const [updatingBlocks, setUpdatingBlocks] = useState([]);
  const {
    fieldErrors,
    fieldErrorMessages,
    getFieldErrors,
    generalErrors,
    setBackendErrors,
    setClientFieldErrors,
    clearFieldError,
    clearAllErrors,
  } = useFormValidation({ normalizePath: toSnakeCasePath });
  const [errorSection, setErrorSection] = useState(null);
  // INFO: Bumped per rejected save so the scroll below fires once per submit
  // rather than every time a field error clears.
  const [errorSignal, setErrorSignal] = useState(0);
  const lastScrolledSignal = useRef(0);

  const reportBackendErrors = (err) => {
    setBackendErrors(err.response?.data);
    setErrorSignal((signal) => signal + 1);
  };

  const reportClientErrors = (errors) => {
    setClientFieldErrors(errors);
    setErrorSignal((signal) => signal + 1);
  };

  useEffect(() => {
    if (!errorSignal || errorSignal === lastScrolledSignal.current) return;
    lastScrolledSignal.current = errorSignal;

    // INFO: FormErrorSummary scrolls itself into view, so only chase the first
    // inline error when there is no banner to land on.
    if (generalErrors.length > 0) return;

    document
      .querySelector('.Mui-error')
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [errorSignal, generalErrors]);
  const [formValues, setFormValues] = useState({
    name: '',
    delimiter: '',
    contains_header: '',
    rows_to_skip: '0',
    refund_types: '',
    insurance_splitting_value: [],
    regular_splitting_value: [],
    business_splitting_value: [],
    file_source: '',
    file_destination: '',
    sftp_file_extension: '',
    is_encrypted: '',
    is_sftp_enabled: false,
    outbound_reporting: false,
    outbound_file_extension: '',
    encrypt_outbound: false,
    include_headers_outbound: false,
    reporting_columns: [],
    outbound_file_delimiter: '',
    outbound_file_date_format: '',
    generate_acknowledgement_file: false,
    generate_daily_report: false,
    acknowledgement_reporting_columns: [],
    sftp_destinations: [],
    immediate_payment_designators: [],
    audience_designators: [],
    facility_id: '',
    is_single_audience: false,
    audience_id: '',
    client_bank_account_id: null,
    bank_account_display_name: null,
    bank_account_business_payee_name: null,
    bank_account_last_four: null,
    organization_id: null,
    custom_payment_type_ids: [],
    custom_payment_types_mappings: {},
    client_sftp_connection_id: null,
  });

  const [originalFormValues, setOriginalFormValues] = useState({});
  const [allowedRefundTypes, setAllowedRefundTypes] = useState([]);
  const navigate = useNavigate();
  const [availableColumns, setAvailableColumns] = useState([]);
  const [availableLineItemColumns, setAvailableLineItemColumns] = useState([]);
  const [dataMappingColumns, setDataMappingColumns] = useState([]);
  const classes = useStyles();
  const regularInputList = [];
  const insuranceInputList = [];
  const businessInputList = [];
  const { id: editId } = useParams();
  const posthog = usePostHog();
  const isNewCustomPaymentTypesWorkflowEnabled = posthog?.isFeatureEnabled(
    'new-custom-payment-types-workflow',
  );
  const [sftpConnections, setSftpConnections] = useState([]);

  const { customPaymentTypes } = useGetCustomPaymentTypesForForm(
    isNewCustomPaymentTypesWorkflowEnabled,
  );

  useEffect(() => {
    // INFO: The endpoint resolves the organization server-side, so this must
    // not wait on organization_id, which is only populated in edit mode.
    const fetchSftpConnections = async () => {
      try {
        const response = await SftpConnectionsApi.getSftpConnectionsForForm();
        setSftpConnections(response.data || []);
      } catch (error) {
        console.error('Failed to fetch SFTP connections', error);
      }
    };
    fetchSftpConnections();
  }, []);

  const confirmChanges = (scope) => {
    return confirm(`Are you sure you want to save ${scope}?`);
  };

  const addSplittingValue = (type) => {
    const splittingType = mapper[type].splittingValue;
    clearFieldError(splittingType);
    setFormValues((values) => {
      return {
        ...values,
        [splittingType]: [...values[splittingType], ''],
      };
    });
  };

  const onSplittingValueChange = (e, index, type) => {
    const splittingType = mapper[type].splittingValue;
    clearFieldError(splittingType);
    const newSplittingValues = formValues[
      splittingType
    ].map((splittingValue, i) =>
      i === index ? e.target.value : splittingValue,
    );
    setFormValues({
      ...formValues,
      [splittingType]: newSplittingValues,
    });
    ``;
  };

  const getAvailableColumns = async () => {
    try {
      const response = await axios.get(
        API_BASE_URL + '/data-mappings/columns-for-reporting',
      );
      const { columnsForReporting, columnsForLineItems } = response.data;
      setAvailableColumns(columnsForReporting);
      setAvailableLineItemColumns(columnsForLineItems);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      getAvailableColumns();
    };
    fetchData();
  }, []);

  const removeSplittingValue = (index, type) => {
    const splittingType = mapper[type].splittingValue;
    clearFieldError(splittingType);
    const newSplittingValues = formValues[splittingType].filter(
      (_, i) => i !== index,
    );
    setFormValues({
      ...formValues,
      [splittingType]: newSplittingValues,
    });
  };

  const onInputChange = (e, type) => {
    setFormValues((values) => {
      const dataMappingType = mapper[type].dataMappingType;
      return {
        ...values,
        [dataMappingType]: {
          ...values[dataMappingType],
          structure: {
            ...values[dataMappingType].structure,
            [e.target.name]: e.target.value,
          },
        },
      };
    });
  };

  const getDefaultDataMappingValues = async () => {
    try {
      const response = await axios.get(
        API_BASE_URL + '/data-mappings/default-data-mapping',
      );

      const columnKeys = Object.keys(response.data);
      const formattedColumns = columnKeys.map((key) => ({
        value: key,
        name: key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
      }));
      setDataMappingColumns(formattedColumns);
      if (editId) return;

      setFormValues((values) => ({
        ...values,
        regular_data_mapping: { structure: { ...response.data } },
        insurance_data_mapping: { structure: { ...response.data } },
        business_data_mapping: { structure: { ...response.data } },
      }));
    } catch (err) {
      console.error(err);
    }
  };

  const getDataMapping = async (id) => {
    try {
      const response = await axios.get(
        API_BASE_URL + '/data-mappings/get-data-mapping-by-id/' + id,
      );

      const formValues = {
        ...response.data,
        is_encrypted: response.data.is_encrypted.toString(),
        is_sftp_enabled: response.data.is_sftp_enabled,
        contains_header: response.data.contains_header.toString(),
        refund_types: response.data.refund_types ?? [],
        reporting_columns: response.data.reporting_columns || [],
        encrypt_outbound: response.data.encrypt_outbound.toString(),
        include_headers_outbound: response.data.include_headers_outbound.toString(),
        generate_acknowledgement_file: !!response.data
          .generate_acknowledgement_file,
        generate_daily_report: !!response.data.generate_daily_report,
        acknowledgement_reporting_columns:
          response.data.acknowledgement_reporting_columns || [],
        sftp_destinations: fromSftpDestinationsResponse(
          response.data.sftp_destinations || [],
        ),
        immediate_payment_designators:
          response.data.immediate_payment_designators || [],
        audience_designators: response.data.audience_designators || [],
        is_single_audience: !!response.data.is_single_audience,
        audience_id: response.data.audience_id || '',
        client_bank_account_id: response.data.client_bank_account_id || null,
        bank_account_display_name:
          response.data.bank_account_display_name || null,
        bank_account_business_payee_name:
          response.data.bank_account_business_payee_name || null,
        bank_account_last_four: response.data.bank_account_last_four || null,
        organization_id: response.data.organization_id || null,
        custom_payment_type_ids: response.data.custom_payment_type_ids || [],
        custom_payment_types_mappings:
          response.data.custom_payment_types_mappings || {},
        client_sftp_connection_id:
          response.data.client_sftp_connection_id || null,
      };

      setFormValues({
        ...formValues,
      });

      setOriginalFormValues({ ...formValues });
    } catch (err) {
      console.error(err);
    }
  };

  const getAllowedRefundTypes = async () => {
    try {
      const response = await RefundsApi.getAllowedRefundTypes();
      setAllowedRefundTypes(response.data.allowed_refund_types);

      setFormValues((prev) => ({
        ...prev,
        allowedRefundTypes: response.data.allowed_refund_types,
      }));
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (editId) {
        await getDataMapping(editId);
      }
      getDefaultDataMappingValues();
      getAllowedRefundTypes();
    };
    fetchData();
  }, [editId]);

  const handleInputChange = (e) => {
    clearFieldError(e.target.name);
    if (DESTINATION_COVERAGE_FIELDS.has(e.target.name)) {
      clearFieldError('sftp_destinations');
    }
    setFormValues((fs) => {
      return { ...fs, [e.target.name]: e.target.value };
    });
  };

  const clearSftpDestinationErrors = (prefix) =>
    Object.keys(fieldErrorMessages)
      .filter((field) => field.startsWith(prefix))
      .forEach(clearFieldError);

  const handleSftpDestinationsChange = (destinations, changedPath) => {
    if (changedPath === 'sftp_destinations') {
      clearSftpDestinationErrors('sftp_destinations');
    } else {
      clearFieldError('sftp_destinations');
      clearFieldError(changedPath.match(/^sftp_destinations\[\d+\]/)?.[0]);
      clearSftpDestinationErrors(changedPath);
    }
    setFormValues((fs) => ({ ...fs, sftp_destinations: destinations }));
  };

  const sftpDestinationErrors = () =>
    validateSftpDestinations(
      formValues.sftp_destinations,
      {
        outboundReporting: !!formValues.outbound_reporting,
        generateAcknowledgementFile: !!formValues.generate_acknowledgement_file,
        generateDailyReport: !!formValues.generate_daily_report,
      },
      t,
    );

  const createDataMapping = async () => {
    clearAllErrors();
    setErrorSection('data-mapping');

    if (isNewCustomPaymentTypesWorkflowEnabled) {
      if (!formValues.custom_payment_type_ids?.length) {
        reportClientErrors({
          custom_payment_type_ids:
            'You need to select at least one custom payment type.',
        });
        return;
      }
    } else if (!formValues.refund_types?.length) {
      reportClientErrors({
        refund_types: 'You need to select at least one payee category type.',
      });
      return;
    }

    if (
      formValues.audience_designators?.some(
        (designator) => !designator || designator.trim() === '',
      )
    ) {
      reportClientErrors({
        audience_designators:
          'Please select a column for all audience designator dropdowns or remove empty ones.',
      });
      return;
    }

    const destinationErrors = sftpDestinationErrors();
    if (Object.keys(destinationErrors).length > 0) {
      reportClientErrors(destinationErrors);
      return;
    }

    try {
      await axios.post(
        API_BASE_URL + '/data-mappings/create-data-mapping-group',
        {
          ...formValues,
          sftp_destinations: toSftpDestinationsPayload(
            formValues.sftp_destinations,
          ),
        },
        { skipValidationToast: true },
      );
      setErrorSection(null);
      navigate('/facility/manage-data-mapping');
    } catch (err) {
      reportBackendErrors(err);
    }
  };

  const updateSftpConfiguration = async () => {
    if (!editId) return;
    if (!confirmChanges('SFTP Pickup Configuration')) return;

    clearAllErrors();
    setErrorSection('sftp-configuration');

    try {
      await axios.put(
        API_BASE_URL +
          '/data-mappings/' +
          '/update-sftp-configuration/' +
          editId,
        {
          is_sftp_enabled: formValues.is_sftp_enabled,
          file_source: formValues.file_source,
          file_destination: formValues.file_destination,
          sftp_file_extension: formValues.sftp_file_extension,
          is_encrypted: formValues.is_encrypted,
          client_sftp_connection_id: formValues.client_sftp_connection_id,
        },
        { skipValidationToast: true },
      );
      setErrorSection(null);
      setUpdatingBlocks((updates) => {
        return updates.filter((block) => block !== 'sftp-configuration');
      });
    } catch (err) {
      reportBackendErrors(err);
    }
  };

  const updateFileConfiguration = async () => {
    if (!editId) return;
    if (!confirmChanges('File Configuration')) return;

    clearAllErrors();
    setErrorSection('file-configuration');

    try {
      await axios.put(
        API_BASE_URL +
          '/data-mappings/' +
          '/update-file-configuration/' +
          editId,
        {
          name: formValues.name,
          delimiter: formValues.delimiter,
          contains_header: formValues.contains_header,
          rows_to_skip: formValues.rows_to_skip,
          facility_id: formValues.facility_id,
        },
        { skipValidationToast: true },
      );
      setErrorSection(null);
      setUpdatingBlocks((updates) => {
        return updates.filter((block) => block !== 'file-configuration');
      });
    } catch (err) {
      reportBackendErrors(err);
    }
  };

  const onFileConfigurationCancel = () => {
    setUpdatingBlocks((updates) => {
      return updates.filter((block) => block !== 'file-configuration');
    });

    setFormValues((values) => {
      return {
        ...values,
        name: originalFormValues.name,
        delimiter: originalFormValues.delimiter,
        contains_header: originalFormValues.contains_header,
        rows_to_skip: originalFormValues.rows_to_skip,
        facility_id: originalFormValues.facility_id,
      };
    });
  };

  const onSftpConfigurationCancel = () => {
    setUpdatingBlocks((updates) => {
      return updates.filter((block) => block !== 'sftp-configuration');
    });

    setFormValues((values) => {
      return {
        ...values,
        is_sftp_enabled: originalFormValues.is_sftp_enabled,
        file_source: originalFormValues.file_source,
        file_destination: originalFormValues.file_destination,
        sftp_file_extension: originalFormValues.sftp_file_extension,
        is_encrypted: originalFormValues.is_encrypted,
        client_sftp_connection_id: originalFormValues.client_sftp_connection_id,
      };
    });
  };

  const onDataMappingCancel = () => {
    setUpdatingBlocks((updates) => {
      return updates.filter((block) => block !== 'data-mapping');
    });

    setFormValues((values) => {
      return {
        ...values,
        refund_types: originalFormValues.refund_types,
        insurance_splitting_value: originalFormValues.insurance_splitting_value,
        regular_splitting_value: originalFormValues.regular_splitting_value,
        business_splitting_value: originalFormValues.business_splitting_value,
        regular_data_mapping: { ...originalFormValues.regular_data_mapping },
        insurance_data_mapping: {
          ...originalFormValues.insurance_data_mapping,
        },
        immediate_payment_designators:
          originalFormValues.immediate_payment_designators,
        audience_designators: originalFormValues.audience_designators,
        is_single_audience: originalFormValues.is_single_audience,
        audience_id: originalFormValues.audience_id,
        custom_payment_type_ids: originalFormValues.custom_payment_type_ids,
        custom_payment_types_mappings:
          originalFormValues.custom_payment_types_mappings,
      };
    });
  };

  const updateDataMapping = async () => {
    if (!editId) return;
    if (!confirmChanges('Data Mapping')) return;

    clearAllErrors();
    setErrorSection('data-mapping');

    if (
      formValues.audience_designators?.some(
        (designator) => !designator || designator.trim() === '',
      )
    ) {
      reportClientErrors({
        audience_designators:
          'Please select a column for all audience designator dropdowns or remove empty ones.',
      });
      return;
    }

    try {
      await axios.put(
        API_BASE_URL + '/data-mappings/update-data-mapping/' + editId,
        {
          refund_types: formValues.refund_types,
          insurance_splitting_value: formValues.insurance_splitting_value,
          regular_splitting_value: formValues.regular_splitting_value,
          business_splitting_value: formValues.business_splitting_value,
          regular_data_mapping: formValues.regular_data_mapping,
          business_data_mapping: formValues.business_data_mapping,
          insurance_data_mapping: {
            ...formValues.insurance_data_mapping,
            exceptions: formValues.insurance_data_mapping?.exceptions?.map(
              (exception) => {
                exception.contains = exception.contains.filter(
                  (value) => value !== '',
                );
                return exception;
              },
            ),
          },
          immediate_payment_designators: formValues.immediate_payment_designators.filter(
            (value) => value !== '',
          ),
          audience_designators: formValues.audience_designators.filter(
            (value) => value !== '',
          ),
          is_single_audience: formValues.is_single_audience,
          audience_id: formValues.audience_id,
          custom_payment_type_ids: formValues.custom_payment_type_ids,
          custom_payment_types_mappings:
            formValues.custom_payment_types_mappings,
        },
        { skipValidationToast: true },
      );
      setErrorSection(null);
      setUpdatingBlocks((updates) => {
        return updates.filter((block) => block !== 'data-mapping');
      });
      if (editId) {
        await getDataMapping(editId);
      }
    } catch (err) {
      reportBackendErrors(err);
    }
  };

  const updateOutboundReporting = async () => {
    if (!editId) return;

    clearAllErrors();
    setErrorSection('outbound-reporting');

    const destinationErrors = sftpDestinationErrors();
    if (Object.keys(destinationErrors).length > 0) {
      reportClientErrors(destinationErrors);
      return;
    }

    if (!confirmChanges('Outbound Reporting')) return;

    try {
      await axios.put(
        API_BASE_URL +
          '/data-mappings/' +
          '/update-outbound-reporting/' +
          editId,
        {
          ...pickOutboundReportingFields(formValues),
          sftp_destinations: toSftpDestinationsPayload(
            formValues.sftp_destinations,
          ),
        },
        { skipValidationToast: true },
      );
      setErrorSection(null);
      setOriginalFormValues((values) => ({
        ...values,
        ...pickOutboundReportingFields(formValues),
      }));
      setUpdatingBlocks((updates) => {
        return updates.filter((block) => block !== 'outbound-reporting');
      });
    } catch (err) {
      reportBackendErrors(err);
    }
  };

  const updateBankAccountLinkage = () => {
    if (!editId) return;

    const confirmMessage = t('dataMappings.bankAccountLinkage.confirmSave');

    requestConfirmation(confirmMessage, async () => {
      clearAllErrors();
      setErrorSection('bank-account-linkage');

      try {
        await DataMappingsApi.updateBankAccountLinkage(
          editId,
          formValues.client_bank_account_id,
          { skipValidationToast: true },
        );

        setErrorSection(null);
        setUpdatingBlocks((updates) => {
          return updates.filter((block) => block !== 'bank-account-linkage');
        });

        if (editId) {
          await getDataMapping(editId);
        }
      } catch (err) {
        reportBackendErrors(err);
      }
    });
  };

  const onBankAccountLinkageCancel = () => {
    setUpdatingBlocks((updates) => {
      return updates.filter((block) => block !== 'bank-account-linkage');
    });

    setFormValues((values) => {
      return {
        ...values,
        client_bank_account_id: originalFormValues.client_bank_account_id,
        bank_account_display_name: originalFormValues.bank_account_display_name,
        bank_account_business_payee_name:
          originalFormValues.bank_account_business_payee_name,
        bank_account_last_four: originalFormValues.bank_account_last_four,
      };
    });
  };

  const onOutboundReportingCancel = () => {
    clearAllErrors();
    setUpdatingBlocks((updates) => {
      return updates.filter((block) => block !== 'outbound-reporting');
    });

    setFormValues((values) => ({
      ...values,
      ...pickOutboundReportingFields(originalFormValues),
    }));
  };

  const addReportingColumnGroup = (columnType = 'reporting_columns') => {
    clearFieldError(columnType);
    setFormValues((prev) => ({
      ...prev,
      [columnType]: [
        ...(prev[columnType] || []),
        { column_name: '', variable_name: '', format: '' },
      ],
    }));
  };

  const updateReportingColumnGroup = (columnType, idx, key, value) => {
    clearFieldError(columnType);
    setFormValues((prev) => {
      const updated = (prev[columnType] || []).map((col, i) =>
        i === idx ? { ...col, [key]: value } : col,
      );
      return { ...prev, [columnType]: updated };
    });
  };

  const removeReportingColumnGroup = (columnType, idx) => {
    clearFieldError(columnType);
    setFormValues((prev) => {
      const filtered = (prev[columnType] || []).filter((_, i) => i !== idx);
      return { ...prev, [columnType]: filtered };
    });
  };

  const isUpdating = (block) => updatingBlocks.includes(block) || !editId;

  const copyDataMappingFrom = (typeFrom) => {
    const dataMappingType =
      typeFrom === 'regular'
        ? 'insurance_data_mapping'
        : 'regular_data_mapping';
    const otherDataMappingType =
      typeFrom === 'regular'
        ? 'regular_data_mapping'
        : 'insurance_data_mapping';

    setFormValues((values) => {
      return {
        ...values,
        [dataMappingType]: {
          structure: { ...values[otherDataMappingType]?.structure },
        },
      };
    });
  };

  const generateInput = (key, type) => {
    return (
      <GridItem key={key} style={{ margin: '10px', minWidth: '30%' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
          }}>
          <div
            onClick={() => {
              navigator.clipboard.writeText(`{{${key}}}`);
              toast.success(t('createEditDataMappings.copiedToClipboard'), {
                autoClose: 1000,
              });
            }}
            style={{ display: 'flex', alignItems: 'center' }}>
            <label style={labelStyle}>
              {' '}
              {key} - {'{{'}
              {key}
              {'}}'}
            </label>{' '}
            <AssignmentOutlinedIcon
              style={{ color: '#3AB795', fontSize: '20px', cursor: 'copy' }}
            />
          </div>

          <TextField
            fullWidth={true}
            disabled={!isUpdating('data-mapping')}
            key={key}
            variant='outlined'
            color='primary'
            className={classes.root}
            placeholder={key}
            multiline
            inputProps={{
              style: {
                outlineColor: '#00bcd4',
                borderColor: '#00bcd4',
                fontSize: '14px',
                lineHeight: '1',
                width: '300px',
              },
            }}
            minRows={1}
            maxRows={1}
            value={
              formValues?.[
                type === 'regular'
                  ? 'regular_data_mapping'
                  : type === 'business'
                  ? 'business_data_mapping'
                  : 'insurance_data_mapping'
              ]?.structure?.[key]
            }
            onChange={(e) => onInputChange(e, type)}
            name={key}
          />
        </div>
      </GridItem>
    );
  };

  useEffect(() => {
    // default exceptions value
    // [{"subStatus": "Payee Exception", "columnName": "business_payee_name", "contains": [], "operation": "="}]

    if (!formValues.insurance_data_mapping?.exceptions?.length) {
      setFormValues({
        ...formValues,
        insurance_data_mapping: {
          ...formValues.insurance_data_mapping,
          exceptions: [
            {
              subStatus: 'Payee Exception',
              columnName: 'business_payee_name',
              contains: [],
              operation: '=',
            },
          ],
        },
      });
    }
  }, [formValues.insurance_data_mapping]);

  const onExceptionChange = (e, index) => {
    const exceptionValues = (
      formValues.insurance_data_mapping.exceptions?.at(0)?.contains ?? []
    ).map((exceptionValue, i) =>
      i === index ? e.target.value : exceptionValue,
    );
    setFormValues({
      ...formValues,
      insurance_data_mapping: {
        ...formValues.insurance_data_mapping,
        exceptions: [
          {
            ...formValues.insurance_data_mapping.exceptions?.at(0),
            contains: exceptionValues,
          },
        ],
      },
    });
  };

  const removeException = (index) => {
    const exceptions = formValues.insurance_data_mapping.exceptions
      ?.at(0)
      ?.contains.filter((_, i) => i !== index);
    setFormValues({
      ...formValues,
      insurance_data_mapping: {
        ...formValues.insurance_data_mapping,
        exceptions: [
          {
            ...formValues.insurance_data_mapping.exceptions?.at(0),
            contains: exceptions,
          },
        ],
      },
    });
  };
  if (
    formValues?.regular_data_mapping &&
    !isNewCustomPaymentTypesWorkflowEnabled
  ) {
    for (const key of Object.keys(formValues.regular_data_mapping.structure)) {
      regularInputList.push(generateInput(key, 'regular'));
      insuranceInputList.push(generateInput(key, 'insurance'));
      businessInputList.push(generateInput(key, 'business'));
    }
  }

  const handleCopyFromTo = (from, to) => {
    const mapper = {
      regular: 'regular_data_mapping',
      insurance: 'insurance_data_mapping',
      business: 'business_data_mapping',
    };

    const dataMappingFrom = mapper[from];
    const dataMappingTo = mapper[to];

    setFormValues((values) => {
      return {
        ...values,
        [dataMappingTo]: {
          structure: { ...values[dataMappingFrom]?.structure },
        },
      };
    });
  };

  // INFO: The create POST spans every section at once, so a field error whose
  // path has no rendered input would otherwise be invisible.
  // INFO: Memoized because FormErrorSummary scrolls itself into view whenever
  // this array's identity changes, which would drag the user back to the top
  // on every keystroke.
  const createModeSummaryErrors = useMemo(
    () =>
      editId
        ? NO_ERRORS
        : [
            ...generalErrors,
            ...Object.entries(fieldErrorMessages)
              .filter(([field]) => !rendersInline(field))
              .flatMap(([field, messages]) =>
                messages.map((message) => `${field}: ${message}`),
              ),
          ],
    [editId, generalErrors, fieldErrorMessages],
  );

  const sectionGeneralErrors = (block) =>
    errorSection === block ? generalErrors : NO_ERRORS;

  return (
    // INFO: The negative pull tucks the card stack under the absolutely
    // positioned page header, which would also swallow the summary.
    <div
      style={{ marginTop: createModeSummaryErrors.length > 0 ? 0 : '-40px' }}>
      <FormErrorSummary errors={createModeSummaryErrors} />
      <FileConfiguration
        formValues={formValues}
        setFormValues={setFormValues}
        originalFormValues={originalFormValues}
        setUpdatingBlocks={setUpdatingBlocks}
        updatingBlocks={updatingBlocks}
        editId={editId}
        isUpdating={isUpdating}
        onFileConfigurationCancel={onFileConfigurationCancel}
        updateFileConfiguration={updateFileConfiguration}
        handleInputChange={handleInputChange}
        fieldErrors={fieldErrors}
        getFieldErrors={getFieldErrors}
        clearFieldError={clearFieldError}
        generalErrors={sectionGeneralErrors('file-configuration')}
      />
      <SftpPickUp
        formValues={formValues}
        setFormValues={setFormValues}
        originalFormValues={originalFormValues}
        setUpdatingBlocks={setUpdatingBlocks}
        updatingBlocks={updatingBlocks}
        editId={editId}
        isUpdating={isUpdating}
        onSftpConfigurationCancel={onSftpConfigurationCancel}
        updateSftpConfiguration={updateSftpConfiguration}
        handleInputChange={handleInputChange}
        sftpConnections={sftpConnections}
        fieldErrors={fieldErrors}
        getFieldErrors={getFieldErrors}
        generalErrors={sectionGeneralErrors('sftp-configuration')}
      />

      {checkPermission({
        parentKey: 'client_bank_accounts',
        childKey: 'view_bank_accounts',
        permission: 'read',
      }) && (
        <BankAccountLinkage
          formValues={formValues}
          setFormValues={setFormValues}
          setUpdatingBlocks={setUpdatingBlocks}
          updatingBlocks={updatingBlocks}
          editId={editId}
          isUpdating={isUpdating}
          onBankAccountLinkageCancel={onBankAccountLinkageCancel}
          updateBankAccountLinkage={updateBankAccountLinkage}
          organizationId={formValues.organization_id}
          fieldErrors={fieldErrors}
          getFieldErrors={getFieldErrors}
          clearFieldError={clearFieldError}
          generalErrors={sectionGeneralErrors('bank-account-linkage')}
        />
      )}

      <CreateYourMapping
        formValues={formValues}
        onCopyFromTo={handleCopyFromTo}
        setFormValues={setFormValues}
        originalFormValues={originalFormValues}
        setUpdatingBlocks={setUpdatingBlocks}
        updatingBlocks={updatingBlocks}
        editId={editId}
        isUpdating={isUpdating}
        onDataMappingCancel={onDataMappingCancel}
        updateDataMapping={updateDataMapping}
        allowedRefundTypes={allowedRefundTypes}
        regularInputList={regularInputList}
        insuranceInputList={insuranceInputList}
        businessInputList={businessInputList}
        addSplittingValue={addSplittingValue}
        onSplittingValueChange={onSplittingValueChange}
        removeSplittingValue={removeSplittingValue}
        copyDataMappingFrom={copyDataMappingFrom}
        onExceptionChange={onExceptionChange}
        removeException={removeException}
        availableColumns={dataMappingColumns}
        customPaymentTypesForm={customPaymentTypes}
        isNewCustomPaymentTypesWorkflowEnabled={
          isNewCustomPaymentTypesWorkflowEnabled
        }
        fieldErrors={fieldErrors}
        getFieldErrors={getFieldErrors}
        clearFieldError={clearFieldError}
        generalErrors={sectionGeneralErrors('data-mapping')}
      />

      <OutboundReporting
        formValues={formValues}
        setFormValues={setFormValues}
        originalFormValues={originalFormValues}
        setUpdatingBlocks={setUpdatingBlocks}
        updatingBlocks={updatingBlocks}
        editId={editId}
        isUpdating={isUpdating}
        onOutboundReportingCancel={onOutboundReportingCancel}
        updateOutboundReporting={updateOutboundReporting}
        availableColumns={availableColumns}
        availableLineItemColumns={availableLineItemColumns}
        handleInputChange={handleInputChange}
        onSftpDestinationsChange={handleSftpDestinationsChange}
        addReportingColumnGroup={addReportingColumnGroup}
        updateReportingColumnGroup={updateReportingColumnGroup}
        removeReportingColumnGroup={removeReportingColumnGroup}
        sftpConnections={sftpConnections}
        fieldErrors={fieldErrors}
        getFieldErrors={getFieldErrors}
        generalErrors={sectionGeneralErrors('outbound-reporting')}
      />
      {!editId && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            marginTop: '20px',
          }}>
          <Button
            style={{ margin: '10px' }}
            onClick={createDataMapping}
            color='primary'>
            {t('createEditDataMappings.saveMapping')}
          </Button>
        </div>
      )}
    </div>
  );
}
